import { useConvex } from "convex/react";
import { ChevronDown, Eye, EyeOff, Grid3x3, ImagePlus, LayoutGrid, LayoutList, LocateFixed, Plus, Settings, Star, Trash2, Type, Upload, Video, X, Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PanoramaIcon } from "@/icons/PanoramaIcon";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useEditorActions } from "@/hooks/useEditorActions";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import { isElectron, updateHotspot, updatePanorama, trashPanorama } from "@/data";
import { useEditorStore } from "@/store/editorStore";
import type { Tables } from "@/types/database";
import { reprocessPanoramas } from "@/utils/reprocessPanoramas";
import { checkAndRegenerateThumbnails } from "@/utils/thumbnailRegenerator";
import { movePanoramaToTrashFromUrls, type PanoramaRecord } from "@/utils/trashManager";
import { StorageService } from "@/services/storageService";
import { CMSService } from "@/services/cmsService";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { getImageDimensions } from "@/utils/imageDimensions";
import { computeFileHash } from "@/utils/fileHash";
import { normalizeAngle, positionToYawPitch } from "@/utils/coordinateSystem";
import { api } from "../../../convex/_generated/api";

type Panorama = Tables<"panoramas">;
type Hotspot = Tables<"hotspots">;
type HotspotContentRecord = Record<string, unknown>;
type HotspotWorldPosition = { x: number; y: number; z: number };
const hasNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;
const hasValidCMSAction = (value: unknown): boolean => {
  if (!value || typeof value !== "object") return false;
  const action = value as { type?: unknown; datasetId?: unknown; recordIndex?: unknown };
  return (
    action.type === "switch_record" &&
    hasNonEmptyString(action.datasetId) &&
    typeof action.recordIndex === "number"
  );
};

const parseHotspotWorldPosition = (value: unknown): HotspotWorldPosition | null => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return parseHotspotWorldPosition(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    if (
      typeof record.x === "number" &&
      typeof record.y === "number" &&
      typeof record.z === "number"
    ) {
      return { x: record.x, y: record.y, z: record.z };
    }
  }
  return null;
};

const normalizeStoragePath = (value: string): { isAbsolute: boolean; path: string } => {
  if (!value) return { isAbsolute: false, path: value };
  if (value.startsWith("/")) return { isAbsolute: true, path: value };
  if (/^[a-zA-Z]:[\\/]/.test(value)) return { isAbsolute: true, path: value };
  if (value.startsWith("file://")) {
    try {
      const url = new URL(value);
      return { isAbsolute: true, path: decodeURIComponent(url.pathname) };
    } catch {
      return { isAbsolute: false, path: value };
    }
  }
  if (value.startsWith("app://storage/")) {
    return { isAbsolute: false, path: value.replace("app://storage/", "") };
  }
  if (value.startsWith("app:/storage/")) {
    return { isAbsolute: false, path: value.replace("app:/storage/", "") };
  }
  return { isAbsolute: false, path: value };
};

const joinPathSegments = (base: string, leaf: string) => {
  const trimmedBase = base.replace(/\/+$/, "");
  const trimmedLeaf = leaf.replace(/^\/+/, "");
  return `${trimmedBase}/${trimmedLeaf}`;
};

const isLikelyLocalPath = (value: string) =>
  value.startsWith("/") ||
  /^[a-zA-Z]:[\\/]/.test(value) ||
  value.startsWith("app://storage/") ||
  value.startsWith("app:/storage/") ||
  value.startsWith("file://");

const getPanoramaRevealCandidate = (panorama: Panorama): string | null => {
  const data = (panorama.data as Record<string, unknown> | null) || null;
  const dataStoragePath = typeof data?.storage_path === "string" ? data.storage_path : null;
  const dataMainFile = typeof data?.main_file === "string" ? data.main_file : null;
  const explicitOriginalPath = typeof data?.original_file_path === "string" ? data.original_file_path : null;
  const directCandidates = [explicitOriginalPath, panorama.private_file_url, panorama.file_url];

  for (const candidate of directCandidates) {
    if (typeof candidate === "string" && candidate.trim() && isLikelyLocalPath(candidate)) {
      return candidate.trim();
    }
  }

  if (dataStoragePath && dataMainFile) {
    return joinPathSegments(dataStoragePath, dataMainFile);
  }
  if (dataStoragePath) {
    return dataStoragePath;
  }
  return null;
};

// Cached thumbnail component that tries IndexedDB first, then falls back to network URLs
interface CachedThumbnailProps {
  panorama: Panorama;
  isCacheInitialized: boolean;
  projectId: string;
  craftspaceId: string | null;
  getThumbnail: (
    panoramaId: string,
    isWebP?: boolean,
  ) => Promise<{ data: ArrayBuffer; mimeType: string } | null>;
  cacheThumbnailFromUrl: (
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    url: string,
    isWebP?: boolean,
  ) => Promise<void>;
  createBlobURL: (data: ArrayBuffer, mimeType: string) => string;
  revokeBlobURL: (url: string) => void;
}

const CachedThumbnail = ({
  panorama,
  isCacheInitialized,
  projectId,
  craftspaceId,
  getThumbnail,
  cacheThumbnailFromUrl,
  createBlobURL,
  revokeBlobURL,
}: CachedThumbnailProps) => {
  const [cachedThumbnailUrl, setCachedThumbnailUrl] = useState<string | null>(null);
  const [isLoadingCache, setIsLoadingCache] = useState(false);
  const [isReCaching, setIsReCaching] = useState(false);
  const baseThumbnailSrc = panorama.thumbnail_url_webp || panorama.thumbnail_url || null;
  const [activeThumbnailSrc, setActiveThumbnailSrc] = useState<string | null>(baseThumbnailSrc);
  const [thumbnailRetryCount, setThumbnailRetryCount] = useState(0);
  const maxThumbnailRetries = 2;
  const previousBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setActiveThumbnailSrc(panorama.thumbnail_url_webp || panorama.thumbnail_url || null);
    setThumbnailRetryCount(0);
  }, [panorama.id, panorama.thumbnail_url_webp, panorama.thumbnail_url]);

  // Try to load from cache when component mounts or panorama changes
  useEffect(() => {
    if (!isCacheInitialized || !panorama.id) return;

    const loadFromCache = async () => {
      try {
        setIsLoadingCache(true);

        // Try WebP first, then JPEG
        let cachedThumbnail = await getThumbnail(panorama.id, true); // WebP
        if (!cachedThumbnail) {
          cachedThumbnail = await getThumbnail(panorama.id, false); // JPEG
        }

        if (cachedThumbnail) {
          const blobUrl = createBlobURL(cachedThumbnail.data, cachedThumbnail.mimeType);
          setCachedThumbnailUrl((previousUrl) => {
            previousBlobUrlRef.current = previousUrl;
            return blobUrl;
          });
        }
      } catch (error) {
        console.warn("Failed to load thumbnail from cache:", error);
      } finally {
        setIsLoadingCache(false);
      }
    };

    loadFromCache();
  }, [isCacheInitialized, panorama.id, getThumbnail, createBlobURL]);

  useEffect(() => {
    const previousUrl = previousBlobUrlRef.current;
    if (previousUrl && previousUrl !== cachedThumbnailUrl) {
      revokeBlobURL(previousUrl);
      previousBlobUrlRef.current = null;
    }
  }, [cachedThumbnailUrl, revokeBlobURL]);

  // Function to re-cache thumbnail from network URL
  const reCacheThumbnail = useCallback(
    async (url: string, isWebP: boolean = false) => {
      if (!isCacheInitialized || !panorama.id || isReCaching || !craftspaceId) return;

      try {
        setIsReCaching(true);
        // console.log(`🔄 Re-caching thumbnail from network: ${panorama.name}`);

        await cacheThumbnailFromUrl(panorama.id, projectId, craftspaceId, url, isWebP);
      } catch (error) {
        console.warn("Failed to re-cache thumbnail:", error);
      } finally {
        setIsReCaching(false);
      }
    },
    [isCacheInitialized, panorama.id, isReCaching, craftspaceId, projectId, cacheThumbnailFromUrl],
  );

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (cachedThumbnailUrl) {
        revokeBlobURL(cachedThumbnailUrl);
      }
    };
  }, [cachedThumbnailUrl, revokeBlobURL]);

  // If we have a cached thumbnail, use it
  if (cachedThumbnailUrl) {
    return (
      <div className="relative group">
        <img
          src={cachedThumbnailUrl}
          alt={`${panorama.name} thumbnail (cached)`}
          className="w-full aspect-video object-cover rounded bg-gray-800"
        />
      </div>
    );
  }

  // Fallback to network URLs if available
  if (panorama.thumbnail_url && activeThumbnailSrc) {
    return (
      <div className="relative group">
        <img
          src={activeThumbnailSrc}
          alt={`${panorama.name} thumbnail`}
          className="w-full aspect-video object-cover rounded bg-gray-800"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          onLoad={() => {
            // Trigger re-caching when network thumbnail loads successfully
            if (activeThumbnailSrc.includes(".webp") && panorama.thumbnail_url_webp) {
              reCacheThumbnail(panorama.thumbnail_url_webp, true);
            } else if (panorama.thumbnail_url) {
              reCacheThumbnail(panorama.thumbnail_url, false);
            }
          }}
          onError={async (e) => {
            // Fallback to JPEG if WebP fails
            const target = e.target as HTMLImageElement;
            if (
              panorama.thumbnail_url_webp &&
              activeThumbnailSrc.includes(panorama.thumbnail_url_webp) &&
              panorama.thumbnail_url
            ) {
              setActiveThumbnailSrc(panorama.thumbnail_url);
              return;
            }

            if (thumbnailRetryCount < maxThumbnailRetries) {
              const baseUrl = panorama.thumbnail_url_webp || panorama.thumbnail_url;
              if (baseUrl) {
                const separator = baseUrl.includes("?") ? "&" : "?";
                const bustedUrl = `${baseUrl}${separator}retry=${Date.now()}-${thumbnailRetryCount + 1}`;
                setTimeout(() => {
                  setThumbnailRetryCount((prev) => prev + 1);
                  setActiveThumbnailSrc(bustedUrl);
                }, 400);
                return;
              }
            }

            // Retries exhausted, try to regenerate
            try {
              const project = useEditorStore.getState().project;
              if (project) {
                const result = await checkAndRegenerateThumbnails(
                  panorama,
                  project.id,
                  project.craftspace_id || "default",
                );

                if (result.regenerated) {
                  // Update the panorama in the store
                  const { updatePanorama } = useEditorStore.getState();
                  updatePanorama(panorama.id, {
                    thumbnail_url: result.thumbnail_url,
                    thumbnail_url_webp: result.thumbnail_url_webp,
                  });

                  // Try to load the new thumbnail
                  if (result.thumbnail_url_webp) {
                    setActiveThumbnailSrc(result.thumbnail_url_webp);
                  } else if (result.thumbnail_url) {
                    setActiveThumbnailSrc(result.thumbnail_url);
                  }
                  return;
                }
              }
            } catch (error) {
              console.warn("Failed to regenerate thumbnails:", error);
            }

            // Show placeholder if regeneration also fails
            target.style.display = "none";
            const placeholder = target.nextElementSibling as HTMLElement;
            if (placeholder) placeholder.classList.remove("hidden");
          }}
        />
        <div className="w-full aspect-video bg-gray-600 rounded border border-gray-600 flex items-center justify-center text-xs text-gray-400 hidden">
          <PanoramaIcon size={14} />
        </div>
      </div>
    );
  }

  // If panorama has thumbnail metadata but active src is temporarily empty, keep placeholder style
  if (panorama.thumbnail_url && !activeThumbnailSrc) {
    return (
      <div className="w-full aspect-video bg-gray-600 rounded flex items-center justify-center text-xs text-gray-400 hover:bg-gray-500 transition-colors duration-200">
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
      </div>
    );
  }

  // Show loading state while checking cache, or placeholder if no thumbnail
  return (
    <div className="w-full aspect-video bg-gray-600 rounded flex items-center justify-center text-xs text-gray-400 hover:bg-gray-500 transition-colors duration-200">
      {isLoadingCache ? (
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
      ) : (
        <PanoramaIcon size={14} />
      )}
    </div>
  );
};

interface SceneHierarchyProps {
  uploadPanoramasImpl?: (
    files: FileList,
    uploadPreference?: "original-only" | "compressed-only",
    mediaType?: "panorama" | "zoomable_image",
  ) => Promise<void>;
  reorderPanoramasImpl?: (panoramaId: string, newOrder: number) => Promise<void>;
  reorderHotspotsImpl?: (hotspotId: string, newOrder: number) => Promise<void>;
}

export const SceneHierarchy = ({
  uploadPanoramasImpl,
  reorderPanoramasImpl,
  reorderHotspotsImpl,
}: SceneHierarchyProps) => {
  const convex = useConvex();
  // Get toast function
  const { toast } = useToast();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const isUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value,
    );
  const shouldUseLocalBackend = (id: string) =>
    isElectron() && (projectBackend === "local" || isUuid(id));
  const isEditableTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName.toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    return target.isContentEditable;
  };

  // Get all state from the store
  const {
    panoramas: panoramasFromStore,
    hotspots,
    currentCameraView,
    selectedPanorama,
    selectedPanoramaIds,
    selectedHotspot,
    placementMode,
    isDraggingToAddHotspot,
    setIsDraggingToAddHotspot,
    setSelectedPanorama,
    setSelectedPanoramaIds,
    setSelectedHotspot,
    setPanoramas,
    setHotspots,
    requestFocusHotspot,
    setPlacementMode,
    setPendingHotspotPlacementId,
    setShowPanoramaProperties,
    skinComponents,
    showHotspots,
    hideHotspots,
    localLoading,
    project,
  } = useEditorStore();
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  // Ensure panoramas is always an array to prevent crashes
  const panoramas = Array.isArray(panoramasFromStore) ? panoramasFromStore : [];
  const hotspotYawByPanorama = useMemo(() => {
    const map = new Map<
      string,
      Array<{ id: string; name: string | null; percent: number }>
    >();
    hotspots.forEach((hotspot) => {
      const panoramaId =
        (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
        (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId ||
        null;
      if (!panoramaId) return;
      const position = parseHotspotWorldPosition(hotspot.position);
      if (!position) return;
      const yaw = positionToYawPitch(position).yaw;
      const percent = Math.min(Math.max((normalizeAngle(yaw) + 180) / 360, 0), 1) * 100;
      const list = map.get(panoramaId) ?? [];
      list.push({ id: hotspot.id, name: hotspot.name ?? null, percent });
      map.set(panoramaId, list);
    });
    return map;
  }, [hotspots]);
  const selectedPanoramaCameraYawPercent =
    selectedPanorama && currentCameraView
      ? Math.min(
          Math.max((normalizeAngle(currentCameraView.yaw) + 180) / 360, 0),
          1,
        ) * 100
      : null;
  const getDefaultViewMarker = useCallback((panorama: Panorama) => {
    const data = panorama.data as Record<string, unknown> | undefined;
    const initialView = data?.initialView as { yaw?: number; pitch?: number } | undefined;
    if (!initialView) return null;
    if (typeof initialView.yaw !== "number" || typeof initialView.pitch !== "number") return null;
    const yawPercent = Math.min(Math.max((normalizeAngle(initialView.yaw) + 180) / 360, 0), 1) * 100;
    const clampedPitch = Math.max(-89, Math.min(89, initialView.pitch));
    const pitchPercent = Math.min(Math.max((90 + clampedPitch) / 180, 0), 1) * 100;
    return { x: yawPercent, y: pitchPercent };
  }, []);

  // Get current location for project ID
  const location = useLocation();
  const projectId = location.pathname.split("/")[1]; // Extract project ID from URL
  const showDebug = useMemo(
    () => new URLSearchParams(location.search).get("debug") === "true",
    [location.search],
  );

  // Initialize IndexedDB cache for thumbnails
  const {
    getThumbnail,
    createBlobURL,
    revokeBlobURL,
    cacheThumbnailFromUrl,
    isInitialized: isCacheInitialized,
  } = useIndexedDBCache();

  // Get actions from custom hook
  const { toggleHotspotVisibility, uploadPanoramas, deleteHotspotWithUndo } =
    useEditorActions(uploadPanoramasImpl);

  // Filter hotspots for the selected panorama
  const currentHotspots = hotspots
    .filter((h) => (selectedPanorama ? h.panorama_id === selectedPanorama.id : true))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const [dragGhostPos, setDragGhostPos] = useState<{ x: number; y: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [panoramaToDelete, setPanoramaToDelete] = useState<Panorama | null>(null);
  const [mediaUploadType, setMediaUploadType] = useState<"panorama" | "zoomable_image">("panorama");
  const [draggedPanorama, setDraggedPanorama] = useState<Panorama | null>(null);
  const [draggedHotspot, setDraggedHotspot] = useState<Hotspot | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    panoramaId: string;
    position: "before" | "after";
  } | null>(null);
  const [hotspotDropIndicator, setHotspotDropIndicator] = useState<{
    hotspotId: string;
    position: "before" | "after";
  } | null>(null);

  // State for inline editing
  const [editingPanoramaId, setEditingPanoramaId] = useState<string | null>(null);
  const [editingHotspotId, setEditingHotspotId] = useState<string | null>(null);
  const [editingPanoramaName, setEditingPanoramaName] = useState<string>("");
  const [editingHotspotName, setEditingHotspotName] = useState<string>("");

  // State for bulk operations
  const [bulkRenamingIds, setBulkRenamingIds] = useState<string[]>([]);
  const [showBulkRenameDialog, setShowBulkRenameDialog] = useState(false);
  const [bulkRenameMode, setBulkRenameMode] = useState<"replace" | "add">("replace");
  const [bulkRenameFindText, setBulkRenameFindText] = useState("");
  const [bulkRenameReplaceText, setBulkRenameReplaceText] = useState("");
  const [bulkRenameAddText, setBulkRenameAddText] = useState("");
  const [bulkRenameAddPosition, setBulkRenameAddPosition] = useState<"before" | "after">("after");
  const [panoramastToDelete, setPanoramastToDelete] = useState<Panorama[]>([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // State for search
  const [panoramaSearchTerm, setPanoramaSearchTerm] = useState<string>("");
  const [panoramaGroupBy, setPanoramaGroupBy] = useState<string | null>(null);
  const [panoramaCMSGroupFields, setPanoramaCMSGroupFields] = useState<Array<{ name: string; displayName: string }>>([]);
  const [panoramaCMSValues, setPanoramaCMSValues] = useState<Map<string, Record<string, unknown>>>(new Map());
  const [hotspotSearchTerm, setHotspotSearchTerm] = useState<string>("");

  // State for panorama view mode
  const [panoramaView, setPanoramaView] = useState<'gallery' | 'grid' | 'grid3' | 'list'>('gallery');

  // Use refs to track current indicators to prevent unnecessary state updates
  const currentDropIndicatorRef = useRef(dropIndicator);
  const currentHotspotDropIndicatorRef = useRef(hotspotDropIndicator);

  // Debounce timers to prevent rapid state updates
  const dropIndicatorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hotspotDropIndicatorTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isFileDrag = useCallback((dt: DataTransfer | null) => {
    if (!dt) return false;
    if (dt.files && dt.files.length > 0) return true;
    if (dt.items && dt.items.length > 0) {
      return Array.from(dt.items).some((item) => item.kind === "file");
    }
    return Array.from(dt.types || []).includes("Files");
  }, []);

  // Update refs when state changes
  useEffect(() => {
    currentDropIndicatorRef.current = dropIndicator;
  }, [dropIndicator]);

  useEffect(() => {
    currentHotspotDropIndicatorRef.current = hotspotDropIndicator;
  }, [hotspotDropIndicator]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (dropIndicatorTimerRef.current) clearTimeout(dropIndicatorTimerRef.current);
      if (hotspotDropIndicatorTimerRef.current) clearTimeout(hotspotDropIndicatorTimerRef.current);
    };
  }, []);

  // Track cursor position for drag ghost when dragging to add hotspot
  useEffect(() => {
    if (!isDraggingToAddHotspot) {
      setDragGhostPos(null);
      document.body.style.cursor = "";
      return;
    }
    document.body.style.cursor = "none";
    const handleMove = (e: PointerEvent) => {
      setDragGhostPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("pointermove", handleMove);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      document.body.style.cursor = "";
    };
  }, [isDraggingToAddHotspot]);
  const showSvgHotspots = false;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  const fileDragDepthRef = useRef(0);
  const [replacingPanoramaId, setReplacingPanoramaId] = useState<string | null>(null);

  // Check if we're on the skin route
  const _isSkinRoute = location.pathname.includes("/skin");
  const _isSettingsRoute = location.pathname.includes("/settings");

  // Log failed panoramas for debugging
  useEffect(() => {
    if (!projectId) return;
    const craftspaceId = project?.craftspace_id || "default";
    let cancelled = false;
    (async () => {
      try {
        const allRecords = await CMSService.loadProjectCMSData(projectId);
        const panoramasRecord = allRecords.find(
          (r) => r.name === "Panoramas" || r.name === "Panoramas Collection",
        );
        if (!panoramasRecord) return;
        const full = await CMSService.loadCMSDataWithContent(panoramasRecord.id, craftspaceId);
        if (cancelled) return;
        const fieldStructure = (full.data as { fields?: Array<{ name: string; displayName?: string; fieldType?: string }> } | undefined)?.fields ?? [];
        const baseFieldNames = new Set(["id","name","panorama_file","file_url","file_url_webp","thumbnail_url","thumbnail_url_webp","width","height","file_size","order","file_status","processing_status","private_file_url","coordinates","created_at"]);
        const groupFields = fieldStructure
          .filter((f) => !baseFieldNames.has(f.name) && (f.fieldType === "singleselect" || f.fieldType === "text"))
          .map((f) => ({ name: f.name, displayName: f.displayName || f.name }));
        setPanoramaCMSGroupFields(groupFields);
        const rows = (full.data as { rows?: Array<Record<string, unknown>> } | undefined)?.rows ?? [];
        const valuesMap = new Map<string, Record<string, unknown>>();
        for (const row of rows) {
          const id = String(row.id ?? "");
          if (id) valuesMap.set(id, row);
        }
        setPanoramaCMSValues(valuesMap);
      } catch {
        // CMS data not available, grouping won't work — that's fine
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, project?.craftspace_id, panoramas]);

  useEffect(() => {
    const failedPanoramas = panoramas.filter((p) => p.processing_status === "failed");
    if (failedPanoramas.length > 0) {
      console.log(
        "Found failed panoramas:",
        failedPanoramas.map((p) => ({
          name: p.name,
          id: p.id,
          hasThumbnail: !!p.thumbnail_url,
          hasThumbnailWebP: !!p.thumbnail_url_webp,
          processingStatus: p.processing_status,
        })),
      );
    }
  }, [panoramas]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadPanoramas(files, "original-only", mediaUploadType);
    }
  };

  // Allow dropping panorama files anywhere in the editor craftspace.
  useEffect(() => {
    const handleWindowDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      fileDragDepthRef.current += 1;
      setDragOver(true);
    };

    const handleWindowDragOver = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      setDragOver(true);
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (fileDragDepthRef.current === 0) {
        setDragOver(false);
      }
    };

    const handleWindowDrop = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer)) return;
      e.preventDefault();
      fileDragDepthRef.current = 0;
      setDragOver(false);

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        uploadPanoramas(files, "original-only", "panorama");
      }
    };

    window.addEventListener("dragenter", handleWindowDragEnter, true);
    window.addEventListener("dragover", handleWindowDragOver, true);
    window.addEventListener("dragleave", handleWindowDragLeave, true);
    window.addEventListener("drop", handleWindowDrop, true);

    return () => {
      window.removeEventListener("dragenter", handleWindowDragEnter, true);
      window.removeEventListener("dragover", handleWindowDragOver, true);
      window.removeEventListener("dragleave", handleWindowDragLeave, true);
      window.removeEventListener("drop", handleWindowDrop, true);
    };
  }, [isFileDrag]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadPanoramas(files, "original-only", mediaUploadType);
    }
  };

  const handlePanoramaClick = useCallback(
    (panorama: Panorama, e: React.SyntheticEvent) => {
      e.stopPropagation();
      const mouseEvent = e as React.MouseEvent;
      if (mouseEvent.shiftKey && selectedPanorama && selectedPanorama.id !== panorama.id) {
        const anchorIndex = panoramas.findIndex((p) => p.id === selectedPanorama.id);
        const clickedIndex = panoramas.findIndex((p) => p.id === panorama.id);
        if (anchorIndex !== -1 && clickedIndex !== -1) {
          const start = Math.min(anchorIndex, clickedIndex);
          const end = Math.max(anchorIndex, clickedIndex);
          const rangeIds = panoramas.slice(start, end + 1).map((p) => p.id);
          setSelectedPanoramaIds(rangeIds);
          return;
        }
      }
      setSelectedPanorama(panorama);
      setSelectedHotspot(null);
    },
    [panoramas, selectedPanorama, setSelectedPanorama, setSelectedPanoramaIds, setSelectedHotspot],
  );

  const handleHotspotClick = useCallback(
    (hotspot: Hotspot, e: React.SyntheticEvent) => {
      e.stopPropagation();
      let contentValue: Record<string, unknown> = {};
      if (hotspot.content) {
        if (typeof hotspot.content === "string") {
          try {
            contentValue = JSON.parse(hotspot.content) as Record<string, unknown>;
          } catch {
            contentValue = {};
          }
        } else if (typeof hotspot.content === "object") {
          contentValue = { ...(hotspot.content as Record<string, unknown>) };
        }
      }
      const hasPositionFlag = contentValue.positioned === true;
      const isZeroPosition =
        hotspot.position?.x === 0 &&
        hotspot.position?.y === 0 &&
        hotspot.position?.z === 0;
      const isUnpositioned = !hotspot.position || (isZeroPosition && !hasPositionFlag);
      setSelectedHotspot(hotspot);
      if (!isUnpositioned) {
        requestFocusHotspot(hotspot.id);
      }
    },
    [requestFocusHotspot, setSelectedHotspot],
  );

  const handleToggleVisibilityClick = useCallback(
    (hotspot: Hotspot, e: React.MouseEvent) => {
      e.stopPropagation();
      toggleHotspotVisibility(hotspot.id);
    },
    [toggleHotspotVisibility],
  );

  const handleLocateHotspotClick = useCallback(
    (hotspot: Hotspot, e: React.MouseEvent) => {
      e.stopPropagation();
      const panorama = panoramas.find((p) => p.id === hotspot.panorama_id) || null;
      if (panorama && selectedPanorama?.id !== panorama.id) {
        setSelectedPanorama(panorama);
      }
      setSelectedHotspot(hotspot);
      setPendingHotspotPlacementId(hotspot.id);
      setPlacementMode(true);
      toast({
        title: "Place hotspot",
        description: "Click in the panorama to position this hotspot.",
      });
    },
    [
      panoramas,
      selectedPanorama,
      setPendingHotspotPlacementId,
      setPlacementMode,
      setSelectedHotspot,
      setSelectedPanorama,
      toast,
    ],
  );

  const handleDeleteClick = useCallback(
    (hotspot: Hotspot, e: React.MouseEvent) => {
      e.stopPropagation();
      deleteHotspotWithUndo(hotspot.id);
    },
    [deleteHotspotWithUndo],
  );

  const handleConfirmDeletePanorama = useCallback(
    async (panorama?: Panorama) => {
      const targetPanorama = panorama || panoramaToDelete;
      if (targetPanorama) {
        try {
          // First move storage files to trash for safe recovery
          try {
            await movePanoramaToTrashFromUrls(targetPanorama, "User deleted from SceneHierarchy");
          } catch (trashError) {
            console.warn("Move to trash failed (continuing with DB delete):", trashError);
          }

          // Delete from database
          if (shouldUseLocalBackend(targetPanorama.id)) {
            const craftspaceId = project?.craftspace_id || "default";
            await trashPanorama(targetPanorama.id, craftspaceId);
          } else {
            await convex.mutation(api.panoramas.remove, { id: targetPanorama.id });
          }

          // Remove from local state
          const currentPanoramas = useEditorStore.getState().panoramas;
          const updatedPanoramas = currentPanoramas.filter((p) => p.id !== targetPanorama.id);
          setPanoramas(updatedPanoramas);

          // Clear selection if this panorama was selected
          if (selectedPanorama?.id === targetPanorama.id) {
            setSelectedPanorama(null);
          }

          console.log("Successfully deleted panorama:", targetPanorama.name);
        } catch (error) {
          console.error("Error deleting panorama:", error);
        } finally {
          if (!panorama) {
            // Only clear state if called from dialog
            setShowDeleteDialog(false);
            setPanoramaToDelete(null);
          }
        }
      }
    },
    [convex, panoramaToDelete, selectedPanorama, setPanoramas, setSelectedPanorama, project, projectBackend],
  );

  const handleConfirmBulkRenamePanoramas = useCallback(async () => {
    try {
      const panoramastToRename = panoramas.filter((p) => bulkRenamingIds.includes(p.id));

      // Apply renaming to each panorama
      for (const panorama of panoramastToRename) {
        let newName = panorama.name;

        if (bulkRenameMode === "replace" && bulkRenameFindText) {
          newName = newName.replaceAll(bulkRenameFindText, bulkRenameReplaceText);
        } else if (bulkRenameMode === "add" && bulkRenameAddText) {
          if (bulkRenameAddPosition === "before") {
            newName = bulkRenameAddText + newName;
          } else {
            newName = newName + bulkRenameAddText;
          }
        }

        if (newName !== panorama.name) {
          try {
            const previousName = panorama.name;

            // Update local store
            setPanoramas(
              panoramas.map((p) => (p.id === panorama.id ? { ...p, name: newName } : p)),
            );
            if (selectedPanorama?.id === panorama.id) {
              setSelectedPanorama({ ...selectedPanorama, name: newName });
            }

            // Save to database
            if (shouldUseLocalBackend(panorama.id)) {
              const craftspaceId = project?.craftspace_id || "default";
              await updatePanorama({ id: panorama.id, craftspace_id: craftspaceId, name: newName });
            } else {
              await convex.mutation(api.panoramas.update, { id: panorama.id, name: newName });
            }
            useEditorStore.getState().recordPanoramaUpdate(
              panorama.id,
              { name: newName },
              { beforeOverride: { name: previousName } },
            );
          } catch (error) {
            console.error("Error renaming panorama:", panorama.name, error);
          }
        }
      }

      console.log("Successfully renamed", panoramastToRename.length, "panoramas");
    } catch (error) {
      console.error("Error in bulk rename:", error);
    } finally {
      setShowBulkRenameDialog(false);
      setBulkRenamingIds([]);
      setBulkRenameFindText("");
      setBulkRenameReplaceText("");
      setBulkRenameAddText("");
    }
  }, [
    convex,
    bulkRenamingIds,
    bulkRenameMode,
    bulkRenameFindText,
    bulkRenameReplaceText,
    bulkRenameAddText,
    bulkRenameAddPosition,
    panoramas,
    projectBackend,
    project,
    selectedPanorama,
    setPanoramas,
    setSelectedPanorama,
  ]);

  const handleConfirmBulkDeletePanoramas = useCallback(async () => {
    try {
      // Delete each panorama
      for (const panorama of panoramastToDelete) {
        try {
          // First move storage files to trash for safe recovery
          try {
            await movePanoramaToTrashFromUrls(panorama, "User deleted from SceneHierarchy");
          } catch (trashError) {
            console.warn("Move to trash failed (continuing with DB delete):", trashError);
          }

          // Delete from database
          if (shouldUseLocalBackend(panorama.id)) {
            const craftspaceId = project?.craftspace_id || "default";
            await trashPanorama(panorama.id, craftspaceId);
          } else {
            await convex.mutation(api.panoramas.remove, { id: panorama.id });
          }

          // Clear selection if this panorama was selected
          if (selectedPanorama?.id === panorama.id) {
            setSelectedPanorama(null);
          }
        } catch (error) {
          console.error("Error deleting panorama:", panorama.name, error);
        }
      }

      // Remove all deleted panoramas from local state
      const currentPanoramas = useEditorStore.getState().panoramas;
      const deletedIds = new Set(panoramastToDelete.map((p) => p.id));
      const updatedPanoramas = currentPanoramas.filter((p) => !deletedIds.has(p.id));
      setPanoramas(updatedPanoramas);

      // Clear selection
      setSelectedPanoramaIds([]);
      if (selectedPanorama && deletedIds.has(selectedPanorama.id)) {
        setSelectedPanorama(null);
      }

      console.log("Successfully deleted", panoramastToDelete.length, "panoramas");
    } catch (error) {
      console.error("Error in bulk delete:", error);
    } finally {
      setShowBulkDeleteDialog(false);
      setPanoramastToDelete([]);
    }
  }, [convex, panoramastToDelete, selectedPanorama, setPanoramas, setSelectedPanorama, setSelectedPanoramaIds, project, projectBackend]);

  const _handleRetryFailedPanorama = useCallback(async (panorama: Panorama) => {
    console.log("Retrying failed panorama:", panorama.name);
    try {
      const result = await reprocessPanoramas();
      if (result.success) {
        console.log("Successfully retried panorama:", panorama.name);
        // Refresh the panoramas list
        // The store should automatically update when the database changes
      } else {
        console.error("Failed to retry panorama:", result.error);
      }
    } catch (error) {
      console.error("Error retrying panorama:", error);
    }
  }, []);

  const _handleBulkFixThumbnails = useCallback(async () => {
    const craftspaceId = useEditorStore.getState().project?.craftspace_id || "default";

    if (!projectId || !craftspaceId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Project or craftspace information not available",
      });
      return;
    }

    try {
      console.log(`🔄 Fixing thumbnails for ${panoramas.length} panoramas...`);

      let successCount = 0;
      let failCount = 0;

      for (const panorama of panoramas) {
        try {
          const result = await checkAndRegenerateThumbnails(panorama, projectId, craftspaceId);

          if (result.regenerated) {
            // Update the panorama in the store
            const { updatePanorama } = useEditorStore.getState();
            updatePanorama(panorama.id, {
              thumbnail_url: result.thumbnail_url,
              thumbnail_url_webp: result.thumbnail_url_webp,
            });

            // Force UI refresh by clearing any cached thumbnails
            if (result.thumbnail_url || result.thumbnail_url_webp) {
              // Small delay to ensure store update has propagated
              setTimeout(async () => {
                try {
                  await cacheThumbnailFromUrl(
                    panorama.id,
                    projectId,
                    craftspaceId,
                    result.thumbnail_url_webp || result.thumbnail_url,
                    !!result.thumbnail_url_webp,
                  );
                } catch (cacheError) {
                  console.warn(
                    `Failed to cache updated thumbnail for ${panorama.name}:`,
                    cacheError,
                  );
                }
              }, 100);
            }

            successCount++;
            console.log(`✅ Fixed thumbnails for: ${panorama.name}`);
          } else {
            console.log(
              `⏭️ Skipped ${panorama.name} (thumbnails already working or source missing)`,
            );
          }
        } catch (error) {
          console.error(`❌ Failed to fix thumbnails for ${panorama.name}:`, error);
          failCount++;
        }
      }

      toast({
        title: "Thumbnail Fix Complete",
        description: `Successfully fixed ${successCount} thumbnails. ${failCount} failed.`,
      });
    } catch (error) {
      console.error(`❌ Bulk thumbnail fix failed:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Thumbnail fix failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    }
  }, [cacheThumbnailFromUrl, panoramas, projectId, toast]);

  const handleReplaceFileInput = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !replacingPanoramaId) return;

      try {
        const panoramaToReplace = panoramas.find((p) => p.id === replacingPanoramaId);
        if (!panoramaToReplace) return;

        // Get image dimensions
        let imageDimensions = { width: null, height: null };
        try {
          const dimensions = await getImageDimensions(file);
          imageDimensions = { width: dimensions.width, height: dimensions.height };
        } catch (_error) {
          console.warn("Failed to extract image dimensions");
        }

        const craftspaceId = project?.craftspace_id || "default";
        const panoramaName = panoramaToReplace.name;
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");

        // Upload to private storage
        const filePath = StoragePaths.private.panoramaOriginal(
          storageSegments.craftspaceKey,
          storageSegments.projectKey,
          panoramaName,
          `${timestamp}_${safeName}`,
        );

        await StorageService.uploadPrivate("staging", filePath, file, { upsert: true });
        const { data: { signedUrl } } = await StorageService.getPrivateUrl("staging", filePath);

        // Update panorama record
        const updates = {
          private_file_url: signedUrl,
          file_status: "private" as const,
          file_size: file.size,
          width: imageDimensions.width,
          height: imageDimensions.height,
        };

        // Update in database and store
        if (shouldUseLocalBackend(replacingPanoramaId)) {
          await updatePanorama({ id: replacingPanoramaId, craftspace_id: craftspaceId, ...updates });
        } else {
          await convex.mutation(api.panoramas.update, {
            id: replacingPanoramaId,
            ...updates,
          });
        }

        // Update store
        setPanoramas(
          panoramas.map((p) => (p.id === replacingPanoramaId ? { ...p, ...updates } : p))
        );
        if (selectedPanorama?.id === replacingPanoramaId) {
          setSelectedPanorama({ ...selectedPanorama, ...updates });
        }

        toast({
          title: "Replaced",
          description: `"${panoramaToReplace.name}" has been replaced with the new image.`,
        });

        setReplacingPanoramaId(null);
      } catch (error) {
        console.error("Error replacing panorama:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to replace panorama. Please try again.",
        });
      }
    },
    [replacingPanoramaId, panoramas, project, shouldUseLocalBackend, convex, setPanoramas, setSelectedPanorama, selectedPanorama, toast],
  );

  const getOpenInFileManagerLabel = useCallback(() => {
    const platform = window.electronAPI?.platform;
    if (platform === "darwin") return "Open in Finder";
    if (platform === "win32") return "Open in Explorer";
    return "Open in File Manager";
  }, []);

  const handleOpenPanoramaInFileManager = useCallback(
    async (panorama: Panorama) => {
      if (!isElectron() || !window.electronAPI) return;
      try {
        const candidate = getPanoramaRevealCandidate(panorama);
        if (!candidate) {
          toast({
            variant: "destructive",
            title: "File unavailable",
            description: "No local file path was found for this panorama.",
          });
          return;
        }

        const normalized = normalizeStoragePath(candidate);
        const fullPath = normalized.isAbsolute
          ? normalized.path
          : await window.electronAPI.getStoragePath(normalized.path);

        await window.electronAPI.revealPath(fullPath);
      } catch (error) {
        console.error("Failed to reveal panorama file:", error);
        toast({
          variant: "destructive",
          title: "Open failed",
          description: "Could not open this panorama in your file manager.",
        });
      }
    },
    [toast],
  );

  const getPanoramaInsertionIndex = useCallback(
    (targetPanoramaId: string, position: "before" | "after") => {
      if (!draggedPanorama) return null;
      const draggedIndex = panoramas.findIndex((p) => p.id === draggedPanorama.id);
      const targetIndex = panoramas.findIndex((p) => p.id === targetPanoramaId);
      if (draggedIndex === -1 || targetIndex === -1) {
        return null;
      }

      let insertionIndex = position === "after" ? targetIndex + 1 : targetIndex;
      if (draggedIndex < insertionIndex) {
        insertionIndex -= 1;
      }
      return insertionIndex;
    },
    [draggedPanorama, panoramas],
  );

  // Panorama drag and drop handlers - Simplified approach to prevent flickering
  const handlePanoramaDragStart = useCallback((panorama: Panorama, e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedPanorama(panorama);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", panorama.id);

    // Immediately calculate and show the drop indicator based on current mouse position
    // This gives instant visual feedback when starting to drag
    const panoramaList = e.currentTarget.closest("[data-panorama-list]") as HTMLElement;
    if (panoramaList) {
      const mouseY = e.clientY;
      const panoramaElements = panoramaList.querySelectorAll("[data-panorama-id]");
      let targetPanoramaId: string | null = null;
      let position: "before" | "after" = "after";

      // Check if mouse is above the first panorama
      if (panoramaElements.length > 0) {
        const firstElement = panoramaElements[0] as HTMLElement;
        const firstElementRect = firstElement.getBoundingClientRect();

        if (mouseY < firstElementRect.top) {
          const firstElementId = firstElement.getAttribute("data-panorama-id");
          if (firstElementId && firstElementId !== panorama.id) {
            targetPanoramaId = firstElementId;
            position = "before";
          }
        }
      }

      // If not above the first panorama, check which panorama the mouse is over
      if (!targetPanoramaId) {
        for (const element of panoramaElements) {
          const elementRect = element.getBoundingClientRect();
          if (mouseY >= elementRect.top && mouseY <= elementRect.bottom) {
            const elementId = element.getAttribute("data-panorama-id");
            if (elementId && elementId !== panorama.id) {
              targetPanoramaId = elementId;
              // Determine position based on mouse position relative to element center
              const centerY = elementRect.top + elementRect.height / 2;
              position = mouseY < centerY ? "before" : "after";
              break;
            }
          }
        }
      }

      // Set the initial drop indicator only if it results in a real reorder
      if (targetPanoramaId) {
        const insertionIndex = getPanoramaInsertionIndex(targetPanoramaId, position);
        const draggedIndex = panoramas.findIndex((p) => p.id === panorama.id);
        if (insertionIndex !== null && insertionIndex !== draggedIndex) {
          setDropIndicator({ panoramaId: targetPanoramaId, position });
        }
      }
    }
  }, [getPanoramaInsertionIndex, panoramas]);

  const handlePanoramaDragEnd = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDraggedPanorama(null);
    setDropIndicator(null);
  }, []);

  // Unified drag over handler for the entire panorama list
  const handlePanoramaListDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedPanorama) return;

      const panoramaList = e.currentTarget as HTMLElement;
      const mouseY = e.clientY;

      // Find which panorama the mouse is over
      const panoramaElements = panoramaList.querySelectorAll("[data-panorama-id]");
      let targetPanoramaId: string | null = null;
      let position: "before" | "after" = "after";

      // Check if mouse is above the first panorama (to allow dropping at the beginning)
      if (panoramaElements.length > 0) {
        const firstElement = panoramaElements[0] as HTMLElement;
        const firstElementRect = firstElement.getBoundingClientRect();

        // If mouse is above the first panorama, show "before" indicator for the first panorama
        if (mouseY < firstElementRect.top) {
          const firstElementId = firstElement.getAttribute("data-panorama-id");
          if (firstElementId && firstElementId !== draggedPanorama.id) {
            targetPanoramaId = firstElementId;
            position = "before";
          }
        }
      }

      // If not above the first panorama, check which panorama the mouse is over
      if (!targetPanoramaId) {
        for (const element of panoramaElements) {
          const elementRect = element.getBoundingClientRect();
          if (mouseY >= elementRect.top && mouseY <= elementRect.bottom) {
            const elementId = element.getAttribute("data-panorama-id");
            if (elementId && elementId !== draggedPanorama.id) {
              targetPanoramaId = elementId;
              // Determine position based on mouse position relative to element center
              const centerY = elementRect.top + elementRect.height / 2;
              position = mouseY < centerY ? "before" : "after";
              break;
            }
          }
        }
      }

      // If still no target found, check if mouse is below the last panorama
      if (!targetPanoramaId && panoramaElements.length > 0) {
        const lastElement = panoramaElements[panoramaElements.length - 1] as HTMLElement;
        const lastElementRect = lastElement.getBoundingClientRect();

        if (mouseY > lastElementRect.bottom) {
          const lastElementId = lastElement.getAttribute("data-panorama-id");
          if (lastElementId && lastElementId !== draggedPanorama.id) {
            targetPanoramaId = lastElementId;
            position = "after";
          }
        }
      }

      // Always show an indicator when dragging - if no specific target found, show a default one
      if (!targetPanoramaId && panoramaElements.length > 0) {
        // Find the closest panorama to the mouse position
        let closestElement: HTMLElement | null = null;
        let closestDistance = Infinity;

        for (const element of panoramaElements) {
          const elementRect = element.getBoundingClientRect();
          const elementCenterY = elementRect.top + elementRect.height / 2;
          const distance = Math.abs(mouseY - elementCenterY);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestElement = element as HTMLElement;
          }
        }

        if (closestElement) {
          const elementId = closestElement.getAttribute("data-panorama-id");
          if (elementId && elementId !== draggedPanorama.id) {
            targetPanoramaId = elementId;
            const elementRect = closestElement.getBoundingClientRect();
            const centerY = elementRect.top + elementRect.height / 2;
            position = mouseY < centerY ? "before" : "after";
          }
        }
      }

      // Update drop indicator - always show something when dragging
      if (targetPanoramaId) {
        const insertionIndex = getPanoramaInsertionIndex(targetPanoramaId, position);
        const draggedIndex = panoramas.findIndex((p) => p.id === draggedPanorama.id);
        if (insertionIndex === null || insertionIndex === draggedIndex) {
          if (dropIndicator) {
            setDropIndicator(null);
          }
          return;
        }

        const currentIndicator = currentDropIndicatorRef.current;
        if (
          currentIndicator?.panoramaId !== targetPanoramaId ||
          currentIndicator?.position !== position
        ) {
          // Debounce the update to prevent flickering
          if (dropIndicatorTimerRef.current) {
            clearTimeout(dropIndicatorTimerRef.current);
          }
          dropIndicatorTimerRef.current = setTimeout(() => {
            setDropIndicator({ panoramaId: targetPanoramaId, position });
          }, 16); // Reduced debounce time for more responsive feedback
        }
      } else {
        // Only clear indicator if we're truly not over any valid area
        // This prevents the indicator from disappearing unexpectedly
        if (dropIndicator && !panoramaList.contains(e.target as Node)) {
          setDropIndicator(null);
        }
      }
    },
    [draggedPanorama, dropIndicator, getPanoramaInsertionIndex, panoramas],
  );

  // Simplified individual panorama drag over - just prevent default
  const _handlePanoramaDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Simplified drag leave - only clear when leaving the entire list
  const handlePanoramaListDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only clear if we're actually leaving the panorama list area
    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return; // Still within the list
    }

    // Don't clear the indicator immediately - give it a small delay
    // to prevent flickering when moving between child elements
    setTimeout(() => {
      if (!currentTarget.contains(e.relatedTarget as Node)) {
        setDropIndicator(null);
      }
    }, 50);
  }, []);

  // Unified drop handler for the entire panorama list
  const handlePanoramaListDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ignore non-reorder drops (for example, file drops handled globally).
      if (!draggedPanorama || !dropIndicator) {
        return;
      }

      if (!reorderPanoramasImpl) {
        return;
      }

      const insertionIndex = getPanoramaInsertionIndex(
        dropIndicator.panoramaId,
        dropIndicator.position,
      );
      const draggedIndex = panoramas.findIndex((p) => p.id === draggedPanorama.id);
      if (insertionIndex === null || insertionIndex === draggedIndex) {
        setDraggedPanorama(null);
        setDropIndicator(null);
        return;
      }

      console.log("Reordering panorama:", {
        dragged: draggedPanorama.name,
        target: dropIndicator.panoramaId,
        position: dropIndicator.position,
        targetOrder: insertionIndex,
      });

      try {
        await reorderPanoramasImpl(draggedPanorama.id, insertionIndex);
        console.log("Reordering successful");
      } catch (error) {
        console.error("Reordering failed:", error);
      }

      setDraggedPanorama(null);
      setDropIndicator(null);
    },
    [draggedPanorama, panoramas, reorderPanoramasImpl, dropIndicator, getPanoramaInsertionIndex],
  );

  // Individual panorama drop handler - just for compatibility
  const _handlePanoramaDrop = useCallback(async (_targetPanoramaId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Individual drops are now handled by the list-level handler
  }, []);

  // Hotspot drag and drop handlers - Simplified approach to prevent flickering
  const handleHotspotDragStart = useCallback((hotspot: Hotspot, e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedHotspot(hotspot);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", hotspot.id);
  }, []);

  const handleHotspotDragEnd = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDraggedHotspot(null);
    setHotspotDropIndicator(null);
  }, []);

  const reorderHotspotsInCurrentPanorama = useCallback(
    async (draggedHotspotId: string, targetHotspotId: string, position: "before" | "after") => {
      if (!selectedPanorama) return;

      const panoramaHotspots = hotspots
        .filter((h) => h.panorama_id === selectedPanorama.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const draggedIndex = panoramaHotspots.findIndex((h) => h.id === draggedHotspotId);
      const targetIndex = panoramaHotspots.findIndex((h) => h.id === targetHotspotId);
      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      let insertionIndex = position === "after" ? targetIndex + 1 : targetIndex;
      if (draggedIndex < insertionIndex) {
        insertionIndex -= 1;
      }

      if (insertionIndex === draggedIndex) {
        return;
      }

      const reorderedHotspots = [...panoramaHotspots];
      const [draggedItem] = reorderedHotspots.splice(draggedIndex, 1);
      const clampedInsertionIndex = Math.max(0, Math.min(insertionIndex, reorderedHotspots.length));
      reorderedHotspots.splice(clampedInsertionIndex, 0, draggedItem);

      const originalOrderById = new Map(panoramaHotspots.map((h) => [h.id, h.order || 0]));
      const reorderedById = new Map(
        reorderedHotspots.map((hotspot, index) => [hotspot.id, { ...hotspot, order: index }]),
      );

      // Optimistic local update for immediate feedback.
      setHotspots(
        hotspots.map((hotspot) => {
          const reordered = reorderedById.get(hotspot.id);
          return reordered || hotspot;
        }),
      );

      if (reorderHotspotsImpl) {
        await reorderHotspotsImpl(draggedHotspotId, clampedInsertionIndex);
        return;
      }

      const changedHotspots = reorderedHotspots.filter(
        (hotspot, index) => (originalOrderById.get(hotspot.id) || 0) !== index,
      );

      await Promise.all(
        changedHotspots.map((hotspot) =>
          convex.mutation(api.hotspots.update, {
            id: hotspot.id,
            order: reorderedHotspots.findIndex((item) => item.id === hotspot.id),
          }),
        ),
      );
    },
    [selectedPanorama, hotspots, setHotspots, reorderHotspotsImpl, convex],
  );

  const handleHotspotDragOver = useCallback(
    (targetHotspot: Hotspot, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedHotspot || draggedHotspot.id === targetHotspot.id) {
        return;
      }

      const targetRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const offsetY = e.clientY - targetRect.top;
      const position: "before" | "after" = offsetY <= targetRect.height * 0.5 ? "before" : "after";

      const currentIndicator = currentHotspotDropIndicatorRef.current;
      if (
        currentIndicator?.hotspotId === targetHotspot.id &&
        currentIndicator.position === position
      ) {
        return;
      }

      setHotspotDropIndicator({ hotspotId: targetHotspot.id, position });
    },
    [draggedHotspot],
  );

  const handleHotspotDrop = useCallback(
    async (targetHotspotId: string, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedHotspot || !hotspotDropIndicator) {
        return;
      }

      await reorderHotspotsInCurrentPanorama(
        draggedHotspot.id,
        targetHotspotId,
        hotspotDropIndicator.position,
      );

      setDraggedHotspot(null);
      setHotspotDropIndicator(null);
    },
    [draggedHotspot, hotspotDropIndicator, reorderHotspotsInCurrentPanorama],
  );

  // Edit handlers for panorama names
  const handlePanoramaDoubleClick = useCallback((panorama: Panorama, e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Double-click detected on panorama:", panorama.name);
    setEditingPanoramaId(panorama.id);
    setEditingPanoramaName(panorama.name);
  }, []);

  const handlePanoramaNameSave = useCallback(async () => {
    console.log("Attempting to save panorama name:", { editingPanoramaId, editingPanoramaName });
    if (editingPanoramaId && editingPanoramaName.trim()) {
      try {
        const newName = editingPanoramaName.trim();
        const existingPanorama = panoramas.find((p) => p.id === editingPanoramaId);
        const previousName = existingPanorama?.name;
        console.log("Updating panorama name to:", newName);

        // Update local store immediately for responsive UI
        setPanoramas(
          panoramas.map((p) => (p.id === editingPanoramaId ? { ...p, name: newName } : p)),
        );
        if (selectedPanorama?.id === editingPanoramaId) {
          setSelectedPanorama({ ...selectedPanorama, name: newName });
        }

        // Save to database
        if (shouldUseLocalBackend(editingPanoramaId)) {
          const craftspaceId = project?.craftspace_id || "default";
          await updatePanorama({ id: editingPanoramaId, craftspace_id: craftspaceId, name: newName });
        } else {
          await convex.mutation(api.panoramas.update, { id: editingPanoramaId, name: newName });
        }
        useEditorStore.getState().recordPanoramaUpdate(
          editingPanoramaId,
          { name: newName },
          previousName !== undefined ? { beforeOverride: { name: previousName } } : undefined,
        );

        console.log("Panorama name updated successfully");
        setEditingPanoramaId(null);
        setEditingPanoramaName("");
      } catch (error) {
        console.error("Error updating panorama name:", error);
      }
    } else {
      console.log("Save conditions not met:", {
        hasId: !!editingPanoramaId,
        hasName: !!editingPanoramaName.trim(),
      });
    }
  }, [
    convex,
    editingPanoramaId,
    editingPanoramaName,
    panoramas,
    projectBackend,
    selectedPanorama,
    setPanoramas,
    setSelectedPanorama,
  ]);

  const handlePanoramaNameCancel = useCallback(() => {
    setEditingPanoramaId(null);
    setEditingPanoramaName("");
  }, []);

  const handlePanoramaNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handlePanoramaNameSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handlePanoramaNameCancel();
      }
    },
    [handlePanoramaNameSave, handlePanoramaNameCancel],
  );

  // Edit handlers for hotspot names
  const handleHotspotDoubleClick = useCallback((hotspot: Hotspot, e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingHotspotId(hotspot.id);
    setEditingHotspotName(hotspot.name);
  }, []);

  const handleHotspotNameSave = useCallback(async () => {
    if (editingHotspotId && editingHotspotName.trim()) {
      try {
        const newName = editingHotspotName.trim();
        const existingHotspot = hotspots.find((h) => h.id === editingHotspotId);
        const previousName = existingHotspot?.name;

        // Update local store immediately for responsive UI
        setHotspots(hotspots.map((h) => (h.id === editingHotspotId ? { ...h, name: newName } : h)));
        if (selectedHotspot?.id === editingHotspotId) {
          setSelectedHotspot({ ...selectedHotspot, name: newName });
        }

        // Save to database
        if (shouldUseLocalBackend(editingHotspotId)) {
          const craftspaceId = project?.craftspace_id || "default";
          await updateHotspot({ id: editingHotspotId, craftspace_id: craftspaceId, name: newName });
        } else {
          await convex.mutation(api.hotspots.update, { id: editingHotspotId, name: newName });
        }
        useEditorStore.getState().recordHotspotUpdate(
          editingHotspotId,
          { name: newName },
          previousName !== undefined ? { beforeOverride: { name: previousName } } : undefined,
        );

        setEditingHotspotId(null);
        setEditingHotspotName("");
      } catch (error) {
        console.error("Error updating hotspot name:", error);
      }
    }
  }, [
    convex,
    editingHotspotId,
    editingHotspotName,
    hotspots,
    projectBackend,
    selectedHotspot,
    setHotspots,
    setSelectedHotspot,
  ]);

  const handleHotspotNameCancel = useCallback(() => {
    setEditingHotspotId(null);
    setEditingHotspotName("");
  }, []);

  const handleHotspotNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleHotspotNameSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleHotspotNameCancel();
      }
    },
    [handleHotspotNameSave, handleHotspotNameCancel],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        if (selectedHotspot) {
          void deleteHotspotWithUndo(selectedHotspot.id);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedHotspot, deleteHotspotWithUndo]);

  // Add new hotspot handler - uses placement mode like the blue + button
  const handleAddNewHotspot = useCallback(() => {
    if (!selectedPanorama) {
      console.warn("No panorama selected for adding hotspot");
      return;
    }

    // Get placement mode state and actions from store
    const { placementMode, setPlacementMode, setPlacementType } = useEditorStore.getState();

    if (placementMode) {
      // If already in placement mode, cancel it
      setPlacementMode(false);
      toast({
        title: "Placement Mode Cancelled",
        description: "Hotspot placement has been cancelled",
      });
    } else {
      // Unselect any currently selected hotspot
      setSelectedHotspot(null);

      // Enable placement mode for SVG hotspots
      setPlacementType("svg");
      setPlacementMode(true);
    }
  }, [selectedPanorama, toast, setSelectedHotspot]);

  // Add new HTML text hotspot handler - enables placement mode like SVG hotspots
  const handleAddNewHTMLTextHotspot = useCallback(() => {
    if (!selectedPanorama) {
      console.warn("No panorama selected for adding HTML hotspot");
      return;
    }

    // Get placement mode state and actions from store
    const { placementMode, setPlacementMode, setPlacementType, setHtmlPlacementContentType } =
      useEditorStore.getState();

    if (placementMode) {
      // If already in placement mode, cancel it
      setPlacementMode(false);
      toast({
        title: "Placement Mode Cancelled",
        description: "Hotspot placement has been cancelled",
      });
    } else {
      // Unselect any currently selected hotspot
      setSelectedHotspot(null);

      // Enable placement mode for HTML hotspots
      setHtmlPlacementContentType("text");
      setPlacementType("html");
      setPlacementMode(true);
    }
  }, [selectedPanorama, toast, setSelectedHotspot]);

  // Add new HTML icon hotspot handler - enables placement mode like SVG hotspots
  const handleAddNewHTMLIconHotspot = useCallback(() => {
    if (!selectedPanorama) {
      console.warn("No panorama selected for adding HTML hotspot");
      return;
    }

    // Get placement mode state and actions from store
    const { placementMode, setPlacementMode, setPlacementType, setHtmlPlacementContentType } =
      useEditorStore.getState();

    if (placementMode) {
      // If already in placement mode, cancel it
      setPlacementMode(false);
      toast({
        title: "Placement Mode Cancelled",
        description: "Hotspot placement has been cancelled",
      });
    } else {
      // Unselect any currently selected hotspot
      setSelectedHotspot(null);

      // Enable placement mode for HTML hotspots
      setHtmlPlacementContentType("icon");
      setPlacementType("html");
      setPlacementMode(true);
    }
  }, [selectedPanorama, toast, setSelectedHotspot]);

  const handleAddNewVideoHotspot = useCallback(() => {
    if (!selectedPanorama) {
      console.warn("No panorama selected for adding video hotspot");
      return;
    }

    const { placementMode, setPlacementMode, setPlacementType } = useEditorStore.getState();

    if (placementMode) {
      setPlacementMode(false);
      toast({
        title: "Placement Mode Cancelled",
        description: "Hotspot placement has been cancelled",
      });
    } else {
      setSelectedHotspot(null);
      setPlacementType("video");
      setPlacementMode(true);
    }
  }, [selectedPanorama, toast, setSelectedHotspot]);

  // Handle ESC key to cancel placement mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && placementMode) {
        const { setPlacementMode } = useEditorStore.getState();
        setPlacementMode(false);
        toast({
          title: "Placement Mode Cancelled",
          description: "Hotspot placement has been cancelled",
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [placementMode, toast]);

  // Cancel placement mode when component unmounts (page navigation)
  useEffect(() => {
    return () => {
      const { placementMode, setPlacementMode } = useEditorStore.getState();
      if (placementMode) {
        setPlacementMode(false);
      }
    };
  }, []);

  return (
    <>
      {/* Drag ghost — follows cursor when dragging from Add Hotspot button */}
      {isDraggingToAddHotspot && dragGhostPos && (
        <div
          className="fixed z-[9999] pointer-events-none select-none"
          style={{ left: dragGhostPos.x - 20, top: dragGhostPos.y - 20 }}
        >
          <div className="w-10 h-10 bg-blue-600 rounded border-2 border-blue-400 flex items-center justify-center text-white shadow-lg opacity-90">
            <Plus size={16} />
          </div>
        </div>
      )}
      {dragOver && (
        <div className="fixed inset-0 z-[70] pointer-events-none">
          <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-400" />
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="rounded-xl border border-blue-300/60 bg-blue-950/80 px-6 py-4 text-center shadow-xl">
              <div className="text-white text-lg font-semibold">Drop panoramas to upload</div>
              <div className="text-blue-200 text-sm mt-1">
                You can drop files anywhere on this screen
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="h-full flex flex-col">
        {/* Top Half: PANORAMAS */}
        <div className="h-1/2 flex flex-col min-h-0">
        {/* Panoramas Section */}
        <div className="px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              Interactive Media
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPanoramaView('gallery')}
                className={`p-1 rounded transition-colors ${
                  panoramaView === 'gallery' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
                title="Gallery view (1 per row)"
              >
                <LayoutList size={14} />
              </button>
              <button
                onClick={() => setPanoramaView('grid')}
                className={`p-1 rounded transition-colors ${
                  panoramaView === 'grid' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
                title="Grid view (2 per row)"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setPanoramaView('grid3')}
                className={`p-1 rounded transition-colors ${
                  panoramaView === 'grid3' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
                title="Grid view (3 per row)"
              >
                <Grid3x3 size={14} />
              </button>
              <button
                onClick={() => setPanoramaView('list')}
                className={`p-1 rounded transition-colors ${
                  panoramaView === 'list' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
                title="List view (names only)"
              >
                <Type size={14} />
              </button>
            </div>
          </div>
          {/* Upload Area - split button with dropdown */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
            id="panorama-upload"
            multiple
          />
          <input
            ref={replaceFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleReplaceFileInput}
            className="hidden"
            id="panorama-replace"
          />
          <div className={`flex rounded overflow-hidden ${
            dragOver ? "border-2 border-blue-400" : ""
          }`}>
            <button
              type="button"
              className={`flex-1 py-3 px-4 text-xs cursor-grab transition-colors flex items-center justify-start space-x-2 ${
                dragOver
                  ? "bg-blue-600 text-white"
                  : "bg-gray-600 text-gray-300 hover:bg-gray-500"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                } else {
                  const input = document.getElementById("panorama-upload") as HTMLInputElement;
                  if (input) {
                    input.click();
                  }
                }
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              title="Click or drag media to panorama section"
            >
              <div className="w-4 h-4 rounded-full border-[1.5px] border-gray-300 flex items-center justify-center"><Plus size={12} strokeWidth={3} className="text-gray-300" /></div>
              <span className="uppercase tracking-wider font-semibold text-gray-300">Add Media</span>
            </button>
            <div className={`w-px ${dragOver ? "bg-blue-400" : "bg-gray-500"}`} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`px-2 py-3 text-xs transition-colors flex items-center justify-center ${
                    dragOver
                      ? "bg-blue-600 text-white hover:bg-blue-500"
                      : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                  }`}
                  title="More media options"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChevronDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="bottom"
                align="end"
                className="bg-gray-800 border-gray-700 text-gray-200 w-[224px]"
              >
                <DropdownMenuItem
                  className="text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaUploadType("panorama");
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  <PanoramaIcon size={12} className="mr-2" />
                  {projectBackend === "local" ? "Panoramas" : "Panoramas"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaUploadType("zoomable_image");
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  <ImagePlus size={12} className="mr-2" />
                  Zoomable images
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Panorama Search + Group By */}
        <div className="px-4 py-0 flex-shrink-0 group">
          <div className="relative flex items-center gap-1">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search panoramas..."
                value={panoramaSearchTerm}
                onChange={(e) => setPanoramaSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-gray-700 text-white placeholder-gray-500 rounded focus:outline-none"
              />
              {panoramaSearchTerm && (
                <button
                  type="button"
                  onClick={() => setPanoramaSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {panoramaCMSGroupFields.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                      panoramaGroupBy
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-400 hover:text-gray-200"
                    }`}
                    title="Group by field"
                  >
                    <ChevronDown size={12} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  align="end"
                  className="bg-gray-800 border-gray-700 text-gray-200 min-w-[140px]"
                >
                  <DropdownMenuItem
                    className={`text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700 ${!panoramaGroupBy ? "text-white" : ""}`}
                    onClick={() => setPanoramaGroupBy(null)}
                  >
                    No grouping
                  </DropdownMenuItem>
                  {panoramaCMSGroupFields.map((field) => (
                    <DropdownMenuItem
                      key={field.name}
                      className={`text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700 ${panoramaGroupBy === field.name ? "text-blue-400" : ""}`}
                      onClick={() => setPanoramaGroupBy(field.name)}
                    >
                      {field.displayName}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Panorama List */}
        <div
          className="flex-1 overflow-y-auto min-h-0"
          data-panorama-list
          onDragOver={handlePanoramaListDragOver}
          onDragLeave={handlePanoramaListDragLeave}
          onDrop={handlePanoramaListDrop}
          onClick={() => {
            setSelectedHotspot(null);
          }}
          role="listbox"
          aria-label="Panorama list"
          tabIndex={0}
        >
          <div className={`py-2 ${
            panoramaGroupBy ? 'pl-4 pr-2 space-y-0' :
            panoramaView === 'grid' ? 'pl-5 pr-3 grid grid-cols-2 gap-1.5' :
            panoramaView === 'grid3' ? 'pl-5 pr-3 grid grid-cols-3 gap-1' :
            panoramaView === 'list' ? 'pl-4 pr-2 space-y-0.5' :
            'pl-4 pr-2 space-y-2'
          }`}>
            {(() => {
              const filteredPanoramas = panoramas.filter(p => p.name.toLowerCase().includes(panoramaSearchTerm.toLowerCase()));
              let sortedPanoramas = filteredPanoramas;
              if (panoramaGroupBy) {
                sortedPanoramas = [...filteredPanoramas].sort((a, b) => {
                  const aVal = String(panoramaCMSValues.get(a.id)?.[panoramaGroupBy] ?? "");
                  const bVal = String(panoramaCMSValues.get(b.id)?.[panoramaGroupBy] ?? "");
                  if (!aVal && bVal) return 1;
                  if (aVal && !bVal) return -1;
                  return aVal.localeCompare(bVal);
                });
              }
              // Precompute group → panorama IDs for click-to-select-group
              const groupPanoramaIds = new Map<string, string[]>();
              if (panoramaGroupBy) {
                for (const p of sortedPanoramas) {
                  const gLabel = String(panoramaCMSValues.get(p.id)?.[panoramaGroupBy] ?? "") || "Unassigned";
                  if (!groupPanoramaIds.has(gLabel)) groupPanoramaIds.set(gLabel, []);
                  groupPanoramaIds.get(gLabel)!.push(p.id);
                }
              }
              let lastGroupLabel: string | undefined;
              return sortedPanoramas.map((panorama, index) => {
                const groupLabel = panoramaGroupBy
                  ? String(panoramaCMSValues.get(panorama.id)?.[panoramaGroupBy] ?? "") || "Unassigned"
                  : null;
                const showGroupHeader = panoramaGroupBy && groupLabel !== lastGroupLabel;
                if (showGroupHeader) lastGroupLabel = groupLabel ?? undefined;
                return (
                  <div key={panorama.id}>
                    {showGroupHeader && (
                      <div
                        className="px-1 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 border-b border-gray-700 mb-1 cursor-pointer hover:text-gray-200 hover:border-gray-500 select-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          const ids = groupPanoramaIds.get(groupLabel!) ?? [];
                          const first = sortedPanoramas.find(p => ids.includes(p.id));
                          if (first) {
                            useEditorStore.setState({
                              selectedPanorama: first,
                              selectedPanoramaIds: ids,
                            });
                          } else {
                            setSelectedPanoramaIds(ids);
                          }
                          setShowPanoramaProperties(true);
                        }}
                        title={`Select all panoramas in "${groupLabel}"`}
                      >
                        {groupLabel}
                      </div>
                    )}
            {/* Drop indicator line - before first panorama */}
            {!panoramaGroupBy && dropIndicator?.panoramaId === sortedPanoramas[0]?.id &&
              dropIndicator?.position === "before" && index === 0 && (
                <div className="h-0.5 bg-blue-400 rounded-full mx-2 my-1" />
              )}
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="space-y-1">
                {/* Drop indicator line - before panorama (but not for first panorama to avoid duplication) */}
                {!panoramaGroupBy && dropIndicator?.panoramaId === panorama.id &&
                  dropIndicator?.position === "before" &&
                  index > 0 && <div className="h-0.5 bg-blue-400 rounded-full mx-2 my-1" />}
                {/* Panorama Item */}
                {/* biome-ignore lint/a11y/useSemanticElements: contains nested controls */}
                <div
                  data-panorama-id={panorama.id}
                  className={`relative rounded-lg cursor-pointer transition-colors border-2 ${
                    selectedPanoramaIds.includes(panorama.id)
                      ? selectedPanorama?.id === panorama.id
                        ? "bg-blue-600 text-white border-blue-400"
                        : "bg-blue-700 text-white border-blue-500"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600 border-transparent"
                  } ${draggedPanorama?.id === panorama.id ? "opacity-50" : ""} ${
                    panoramaView === 'list' ? 'px-2 py-1' : ''
                  }`}
                  onClick={(e) => handlePanoramaClick(panorama, e)}
                  onKeyDown={(e) => {
                    if (isEditableTarget(e.target)) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePanoramaClick(panorama, e);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  draggable={!panoramaGroupBy}
                  onDragStart={(e) => { if (!panoramaGroupBy) handlePanoramaDragStart(panorama, e); }}
                  onDragEnd={handlePanoramaDragEnd}
                  // Remove individual drag over handler to prevent interference with list-level handling
                >
                  {panoramaView === 'list' ? (
                    // List view: names only
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {editingPanoramaId === panorama.id ? (
                          <input
                            type="text"
                            value={editingPanoramaName}
                            onChange={(e) => setEditingPanoramaName(e.target.value)}
                            onBlur={handlePanoramaNameSave}
                            onKeyDown={handlePanoramaNameKeyDown}
                            className="w-full bg-transparent text-xs text-white border-none outline-none h-4 leading-4 py-0"
                          />
                        ) : (
                          <button
                            type="button"
                            className="text-xs truncate block cursor-pointer text-white bg-transparent border-0 p-0 text-left w-full"
                            onClick={(e) => handlePanoramaDoubleClick(panorama, e)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handlePanoramaDoubleClick(panorama, e);
                              }
                            }}
                          >
                            {panorama.name}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPanorama(panorama);
                          setSelectedHotspot(null);
                          setShowPanoramaProperties(true);
                        }}
                        className="flex-shrink-0 hover:bg-gray-500 p-1 rounded text-gray-300 hover:text-white transition-colors"
                        title="Panorama settings"
                        type="button"
                      >
                        <Settings size={12} className="text-white" />
                      </button>
                    </div>
                  ) : (
                    // Gallery and Grid views: with thumbnail
                    <>
                      {/* Thumbnail with overlay title */}
                      <div className="relative">
                        <CachedThumbnail
                          panorama={panorama}
                          isCacheInitialized={isCacheInitialized}
                          projectId={projectId}
                          craftspaceId={useEditorStore.getState().project?.craftspace_id || null}
                          getThumbnail={getThumbnail}
                          cacheThumbnailFromUrl={cacheThumbnailFromUrl}
                          createBlobURL={createBlobURL}
                          revokeBlobURL={revokeBlobURL}
                        />
                        {showDebug &&
                          (hotspotYawByPanorama.get(panorama.id) || []).map((marker) => {
                            const isSelected = selectedHotspot?.id === marker.id;
                            return (
                              <div
                                key={marker.id}
                                className="pointer-events-none absolute top-0 bottom-0 z-10"
                                style={{
                                  left: `${marker.percent}%`,
                                  width: isSelected ? "4px" : "2px",
                                  transform: "translateX(-50%)",
                                  background: "#ef4444",
                                  boxShadow: "0 0 4px rgba(239, 68, 68, 0.7)",
                                }}
                                title={marker.name || undefined}
                              />
                            );
                          })}
                        {showDebug &&
                          selectedPanorama?.id === panorama.id &&
                          typeof selectedPanoramaCameraYawPercent === "number" && (
                            <div
                              className="pointer-events-none absolute top-0 bottom-0 z-10"
                              style={{
                                left: `${selectedPanoramaCameraYawPercent}%`,
                                width: "2px",
                                transform: "translateX(-50%)",
                                background: "#facc15",
                                boxShadow: "0 0 4px rgba(250, 204, 21, 0.7)",
                              }}
                            />
                          )}
                        {showDebug &&
                          (() => {
                            const marker = getDefaultViewMarker(panorama);
                            if (!marker) return null;
                            return (
                              <div
                                className="pointer-events-none absolute z-10 h-2 w-2 rounded-full bg-sky-400 ring-2 ring-white/70 shadow-[0_0_6px_rgba(56,189,248,0.9)]"
                                style={{
                                  left: `${marker.x}%`,
                                  top: `${marker.y}%`,
                                  transform: "translate(-50%, -50%)",
                                }}
                                title="Default view"
                              />
                            );
                          })()}

                        {/* Panorama Name Overlay */}
                        <div
                          className={`absolute bottom-0 left-0 right-0 bg-black/70 px-1 flex items-center rounded-b cursor-text ${(panoramaView === 'grid' || panoramaView === 'grid3') ? 'h-4' : 'h-5'}`}
                          onClick={(e) => {
                            if (editingPanoramaId === panorama.id) return;
                            handlePanoramaClick(panorama, e);
                            handlePanoramaDoubleClick(panorama, e);
                          }}
                        >
                          {editingPanoramaId === panorama.id ? (
                            <input
                              type="text"
                              value={editingPanoramaName}
                              onChange={(e) => setEditingPanoramaName(e.target.value)}
                              onBlur={handlePanoramaNameSave}
                              onKeyDown={handlePanoramaNameKeyDown}
                              className="w-full bg-transparent text-xs text-white border-none outline-none truncate h-4 leading-4"
                            />
                          ) : (
                            <button
                              type="button"
                              className={`truncate block cursor-pointer text-white bg-transparent border-0 p-0 text-left ${(panoramaView === 'grid' || panoramaView === 'grid3') ? 'text-[10px]' : 'text-xs'}`}
                              onClick={(e) => handlePanoramaDoubleClick(panorama, e)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handlePanoramaDoubleClick(panorama, e);
                                }
                              }}
                            >
                              {panorama.name}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Action buttons - positioned absolutely in top right */}
                      <div className="absolute top-1 right-1 flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPanorama(panorama);
                            setSelectedHotspot(null);
                            setShowPanoramaProperties(true);
                          }}
                          className="hover:bg-gray-500 p-1 rounded text-gray-300 hover:text-white transition-colors"
                          title="Panorama settings"
                          type="button"
                        >
                          <Settings size={12} className="text-white" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {/* Drop indicator line - after panorama (and hotspots if any) */}
                {!panoramaGroupBy && dropIndicator?.panoramaId === panorama.id &&
                  dropIndicator?.position === "after" && (
                    <div className="h-0.5 bg-blue-400 rounded-full mx-2 my-1" />
                  )}
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent className="min-w-[160px]">
                  {selectedPanoramaIds.length <= 1 ? (
                    <>
                      <ContextMenuItem
                        onClick={() => {
                          setSelectedPanorama(panorama);
                          setSelectedHotspot(null);
                          setShowPanoramaProperties(true);
                        }}
                      >
                        Properties
                      </ContextMenuItem>
                      <ContextMenuItem
                        onClick={() => {
                          setEditingPanoramaId(panorama.id);
                          setEditingPanoramaName(panorama.name);
                        }}
                      >
                        Rename
                      </ContextMenuItem>
                      <ContextMenuItem
                        onClick={() => {
                          setReplacingPanoramaId(panorama.id);
                          replaceFileInputRef.current?.click();
                        }}
                      >
                        Replace
                      </ContextMenuItem>
                      {isElectron() && window.electronAPI && (
                        <ContextMenuItem
                          onClick={() => {
                            void handleOpenPanoramaInFileManager(panorama);
                          }}
                        >
                          {getOpenInFileManagerLabel()}
                        </ContextMenuItem>
                      )}
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        className="text-red-400"
                        onClick={() => setPanoramaToDelete(panorama)}
                      >
                        Delete
                      </ContextMenuItem>
                    </>
                  ) : (
                    <>
                      <ContextMenuItem
                        onClick={() => {
                          setBulkRenamingIds(selectedPanoramaIds);
                          setShowBulkRenameDialog(true);
                        }}
                      >
                        Rename all
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        className="text-red-400"
                        onClick={() => {
                          const panoramastToDeleteList = panoramas.filter((p) =>
                            selectedPanoramaIds.includes(p.id),
                          );
                          setPanoramastToDelete(panoramastToDeleteList);
                          setShowBulkDeleteDialog(true);
                        }}
                      >
                        Delete all
                      </ContextMenuItem>
                    </>
                  )}
                </ContextMenuContent>
              </ContextMenu>
                  </div>
                );
              });
            })()}

            {panoramas.filter(p => p.name.toLowerCase().includes(panoramaSearchTerm.toLowerCase())).length === 0 && !localLoading && (
              <div className="text-center py-8">
                <PanoramaIcon size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-sm text-gray-400">{panoramas.length === 0 ? "No panoramas uploaded yet" : "No panoramas match your search"}</p>
              </div>
            )}
          </div>
        </div>
        </div>{/* end top half */}

        <Separator className="bg-gray-700" />

        {/* Bottom Half: HOTSPOTS */}
        <div className="h-1/2 flex flex-col min-h-0">
          {/* Hotspot section header + add buttons */}
          <div className="px-4 py-2 flex-shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">
              Hotspots
            </p>
            {selectedPanorama ? (
              <div className="space-y-0.5">
                {/* Split button: left adds Icon Hotspot, right opens dropdown */}
                <div className={`flex rounded overflow-hidden`}>
                  <button
                    type="button"
                    className={`flex-1 py-3 px-4 text-xs cursor-grab transition-colors flex items-center justify-start space-x-2 ${
                      placementMode
                        ? "bg-blue-600 text-white"
                        : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                    }`}
                    onClick={handleAddNewHTMLIconHotspot}
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      const { setIsDraggingToAddHotspot, setPlacementType, setHtmlPlacementContentType } =
                        useEditorStore.getState();
                      setHtmlPlacementContentType("icon");
                      setPlacementType("html");
                      setIsDraggingToAddHotspot(true);
                    }}
                    title={placementMode ? "Cancel Hotspot Placement" : "Click or drag to panorama to add hotspot"}
                  >
                    {placementMode ? <X size={12} className="text-white" /> : <div className="w-4 h-4 rounded-full border-[1.5px] border-gray-300 flex items-center justify-center"><Plus size={12} strokeWidth={3} className="text-gray-300" /></div>}
                    <span className="uppercase tracking-wider font-semibold text-gray-300">{placementMode ? "Cancel Placement" : "Craft Hotspot"}</span>
                  </button>
                  <div className={`w-px ${placementMode ? "bg-blue-400" : "bg-gray-500"}`} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={`px-2 py-1.5 text-xs transition-colors flex items-center justify-center ${
                          placementMode
                            ? "bg-blue-600 text-white hover:bg-blue-500"
                            : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                        }`}
                        title="More hotspot types"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChevronDown size={12} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      side="bottom"
                      align="end"
                      className="bg-gray-800 border-gray-700 text-gray-200 w-[224px]"
                    >
                      <DropdownMenuItem
                        className="text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                        onClick={handleAddNewHTMLTextHotspot}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          const { setIsDraggingToAddHotspot, setPlacementType, setHtmlPlacementContentType } =
                            useEditorStore.getState();
                          setHtmlPlacementContentType("text");
                          setPlacementType("html");
                          setIsDraggingToAddHotspot(true);
                        }}
                      >
                        <Type size={12} className="mr-2" />
                        Text Hotspot
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                        onClick={handleAddNewHTMLIconHotspot}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          const { setIsDraggingToAddHotspot, setPlacementType, setHtmlPlacementContentType } =
                            useEditorStore.getState();
                          setHtmlPlacementContentType("icon");
                          setPlacementType("html");
                          setIsDraggingToAddHotspot(true);
                        }}
                      >
                        <Star size={12} className="mr-2" />
                        Icon Hotspot
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-xs cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                        onClick={handleAddNewVideoHotspot}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          const { setIsDraggingToAddHotspot, setPlacementType } = useEditorStore.getState();
                          setPlacementType("video");
                          setIsDraggingToAddHotspot(true);
                        }}
                      >
                        <Video size={12} className="mr-2" />
                        Video Hotspot
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">Select a panorama to manage hotspots</p>
            )}
          </div>

          {/* Hotspot Search */}
          {selectedPanorama && (
            <div className="px-4 py-0 flex-shrink-0 group">
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="Search hotspots..."
                  value={hotspotSearchTerm}
                  onChange={(e) => setHotspotSearchTerm(e.target.value)}
                  className="w-full px-2 py-1 text-xs bg-gray-700 text-white placeholder-gray-500 rounded focus:outline-none"
                />
                {hotspotSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setHotspotSearchTerm("")}
                    className="absolute right-2 text-gray-400 hover:text-gray-200 transition-colors"
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Hotspot List */}
          <div
            className="flex-1 overflow-y-auto min-h-0 px-4"
            role="listbox"
            aria-label="Hotspot list"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedHotspot) {
                const lastHotspot = currentHotspots[currentHotspots.length - 1];
                if (lastHotspot) {
                  setHotspotDropIndicator({ hotspotId: lastHotspot.id, position: "after" });
                }
              }
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setHotspotDropIndicator(null);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedHotspot) {
                const lastHotspot = currentHotspots[currentHotspots.length - 1];
                if (lastHotspot) {
                  void reorderHotspotsInCurrentPanorama(draggedHotspot.id, lastHotspot.id, "after");
                  setDraggedHotspot(null);
                  setHotspotDropIndicator(null);
                }
              }
            }}
          >
            <div className="py-2 space-y-1">
              {selectedPanorama && currentHotspots.length > 0 ? (
                currentHotspots.filter(h => h.name.toLowerCase().includes(hotspotSearchTerm.toLowerCase())).map((hotspot) => {
                  const content = (hotspot.content as Record<string, unknown>) || {};
                  const hasLinkedPanorama = hasNonEmptyString(content.linkedPanoramaId);
                  const hasNotification =
                    hasNonEmptyString(content.notificationMessage) ||
                    hasNonEmptyString(content.notificationTitle);
                  const hasDownload = hasNonEmptyString(content.downloadFileUrl);
                  const hasSkinVisibility =
                    Array.isArray(content.skinVisibilityTargetComponentIds) &&
                    content.skinVisibilityTargetComponentIds.length > 0;
                  const hasHotspotVisibility =
                    Array.isArray(content.hotspotVisibilityTargetIds) &&
                    content.hotspotVisibilityTargetIds.length > 0;
                  const hasCMSAction =
                    hasValidCMSAction((hotspot as { cms_action?: unknown }).cms_action) ||
                    hasValidCMSAction((hotspot as { cmsAction?: unknown }).cmsAction);

                  const hasActions =
                    hasLinkedPanorama ||
                    hasNotification ||
                    hasDownload ||
                    hasSkinVisibility ||
                    hasHotspotVisibility ||
                    hasCMSAction;

                  let contentValue: Record<string, unknown> = {};
                  if (hotspot.content) {
                    if (typeof hotspot.content === "string") {
                      try {
                        contentValue = JSON.parse(hotspot.content) as Record<string, unknown>;
                      } catch {
                        contentValue = {};
                      }
                    } else if (typeof hotspot.content === "object") {
                      contentValue = { ...(hotspot.content as Record<string, unknown>) };
                    }
                  }
                  const hasPositionFlag = contentValue.positioned === true;
                  const isZeroPosition =
                    hotspot.position?.x === 0 &&
                    hotspot.position?.y === 0 &&
                    hotspot.position?.z === 0;
                  const isUnpositioned = !hotspot.position || (isZeroPosition && !hasPositionFlag);
                  const htmlContentType = typeof contentValue.contentType === "string" ? contentValue.contentType : null;
                  const hotspotTypeIcon =
                    hotspot.type === "video" ? (
                      <Video size={12} className="text-white flex-shrink-0" />
                    ) : hotspot.type === "svg" ? (
                      <Star size={12} className="text-white flex-shrink-0" />
                    ) : hotspot.type === "text" || htmlContentType === "text" ? (
                      <Type size={12} className="text-white flex-shrink-0" />
                    ) : hotspot.type === "html" && htmlContentType === "icon" ? (
                      <Star size={12} className="text-white flex-shrink-0" />
                    ) : null;

                  return (
                    <div key={hotspot.id} className="space-y-0">
                      {hotspotDropIndicator?.hotspotId === hotspot.id &&
                        hotspotDropIndicator.position === "before" && (
                          <div className="h-0.5 bg-blue-400 rounded-full mx-1 my-0" />
                        )}
                      {/* biome-ignore lint/a11y/useSemanticElements: contains nested controls */}
                      <div
                        className={`p-0.5 rounded text-xs cursor-pointer transition-colors border-2 ${
                          selectedHotspot?.id === hotspot.id
                            ? "bg-blue-500 text-white border-blue-300"
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600"
                        } ${draggedHotspot?.id === hotspot.id ? "opacity-50" : ""}`}
                        onClick={(e) => handleHotspotClick(hotspot, e)}
                        onKeyDown={(e) => {
                          if (isEditableTarget(e.target)) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleHotspotClick(hotspot, e);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => handleHotspotDragStart(hotspot, e)}
                        onDragEnd={handleHotspotDragEnd}
                        onDragOver={(e) => handleHotspotDragOver(hotspot, e)}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleHotspotDrop(hotspot.id, e);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 flex-1 min-w-0">
                            <div className="cursor-move text-gray-400 hover:text-gray-300 flex-shrink-0">
                              {hotspotTypeIcon}
                            </div>
                            {editingHotspotId === hotspot.id ? (
                              <input
                                type="text"
                                value={editingHotspotName}
                                onChange={(e) => setEditingHotspotName(e.target.value)}
                                onBlur={handleHotspotNameSave}
                                onKeyDown={handleHotspotNameKeyDown}
                                className="bg-transparent text-xs text-white border-none outline-none flex-1 min-w-0"
                              />
                            ) : (
                              <button
                                type="button"
                                className="text-xs text-white cursor-pointer overflow-hidden text-ellipsis line-clamp-2 leading-none bg-transparent border-0 p-0 text-left"
                                onDoubleClick={(e) => handleHotspotDoubleClick(hotspot, e)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleHotspotDoubleClick(hotspot, e);
                                  }
                                }}
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-all",
                                  hyphens: "auto",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {hotspot.name}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            {hasActions && (
                              <div title="Has actions">
                                <Zap size={12} className="text-gray-300" />
                              </div>
                            )}
                            {isUnpositioned ? (
                              <Tooltip delayDuration={100}>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => handleLocateHotspotClick(hotspot, e)}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData("application/x-hotspot-relocate", hotspot.id);
                                      e.dataTransfer.effectAllowed = "move";
                                      setSelectedHotspot(hotspot);
                                      setPendingHotspotPlacementId(hotspot.id);
                                      setPlacementMode(true);
                                      setIsDraggingToAddHotspot(true);
                                    }}
                                    onDragEnd={() => setIsDraggingToAddHotspot(false)}
                                    className="hover:bg-gray-500 p-0.5 rounded"
                                    type="button"
                                  >
                                    <LocateFixed size={12} className="text-white" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" align="center" className="bg-gray-900 text-gray-100 border-gray-700">
                                  The hotspot has not been positioned yet.
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <button
                                onClick={(e) => handleToggleVisibilityClick(hotspot, e)}
                                className="hover:bg-gray-500 p-0.5 rounded"
                                type="button"
                              >
                                {hotspot.visible ? (
                                  <Eye size={12} className="text-white" />
                                ) : (
                                  <EyeOff size={12} className="text-white" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteClick(hotspot, e)}
                              className="hover:bg-gray-500 p-0.5 rounded text-gray-300 hover:text-red-400 transition-colors"
                              title="Delete hotspot"
                              type="button"
                            >
                              <Trash2 size={12} className="text-white" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {hotspotDropIndicator?.hotspotId === hotspot.id &&
                        hotspotDropIndicator.position === "after" && (
                          <div className="h-0.5 bg-blue-400 rounded-full mx-2 my-1" />
                        )}
                    </div>
                  );
                })
              ) : selectedPanorama ? (
                <p className="text-xs text-gray-500 text-center py-4">{currentHotspots.length === 0 ? "No hotspots yet" : "No hotspots match your search"}</p>
              ) : null}
            </div>
          </div>
        </div>{/* end bottom half */}

        {/* Panorama Delete Confirmation Dialog */}
        <Dialog
          open={!!panoramaToDelete}
          onOpenChange={(open) => !open && setPanoramaToDelete(null)}
        >
          <DialogContent className="bg-gray-800 border-gray-700 max-w-sm w-full">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Panorama?</DialogTitle>
              <DialogDescription className="text-gray-400">
                This action cannot be undone. The panorama and all associated data will be
                permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="text-gray-300 mb-4">
              Are you sure you want to delete "{panoramaToDelete?.name}"? This action cannot be
              undone.
            </div>
            <div className="text-xs text-gray-400 mb-4">
              This will also delete all hotspots associated with this panorama.
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPanoramaToDelete(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                autoFocus
                onClick={() => handleConfirmDeletePanorama()}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent className="bg-gray-800 border-gray-700 max-w-sm w-full">
            <DialogHeader>
              <DialogTitle className="text-white">Delete {panoramastToDelete.length} Panoramas?</DialogTitle>
              <DialogDescription className="text-gray-400">
                This action cannot be undone. All selected panoramas and their associated data will
                be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="text-gray-300 mb-4">
              Are you sure you want to delete {panoramastToDelete.length} panorama(s)? This action
              cannot be undone.
            </div>
            <div className="text-xs text-gray-400 mb-4">
              This will also delete all hotspots associated with these panoramas.
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setPanoramastToDelete([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                autoFocus
                onClick={() => handleConfirmBulkDeletePanoramas()}
              >
                Delete All
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Rename Dialog */}
        <Dialog open={showBulkRenameDialog} onOpenChange={setShowBulkRenameDialog}>
          <DialogContent className="bg-gray-800 border-gray-700 max-w-lg w-full">
            <DialogHeader>
              <DialogTitle className="text-white">Rename Panorama Items</DialogTitle>
            </DialogHeader>

            <Tabs
              value={bulkRenameMode}
              onValueChange={(value) => setBulkRenameMode(value as "replace" | "add")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 bg-gray-700">
                <TabsTrigger
                  value="replace"
                  className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gray-600"
                >
                  Replace Text
                </TabsTrigger>
                <TabsTrigger
                  value="add"
                  className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gray-600"
                >
                  Add Text
                </TabsTrigger>
              </TabsList>

              <TabsContent value="replace" className="space-y-4 mt-4 focus-visible:ring-0 focus-visible:outline-none">
                <div className="space-y-2">
                  <Label htmlFor="find-text" className="text-gray-300">
                    Find:
                  </Label>
                  <Input
                    id="find-text"
                    autoFocus
                    value={bulkRenameFindText}
                    onChange={(e) => setBulkRenameFindText(e.target.value)}
                    placeholder="Text to find"
                    className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="replace-with-text" className="text-gray-300">
                    Replace with:
                  </Label>
                  <Input
                    id="replace-with-text"
                    value={bulkRenameReplaceText}
                    onChange={(e) => setBulkRenameReplaceText(e.target.value)}
                    placeholder="Replacement text"
                    className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
                  />
                </div>
              </TabsContent>

              <TabsContent value="add" className="space-y-4 mt-4 focus-visible:ring-0 focus-visible:outline-none">
                <div className="space-y-2">
                  <Label className="text-gray-300">Add Text:</Label>
                  <div className="flex gap-2">
                    <Input
                      value={bulkRenameAddText}
                      onChange={(e) => setBulkRenameAddText(e.target.value)}
                      placeholder="Text to add"
                      className="bg-gray-700 border-0 text-white placeholder:text-gray-500 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-0 flex-1"
                    />
                    <select
                      value={bulkRenameAddPosition}
                      onChange={(e) =>
                        setBulkRenameAddPosition(e.target.value as "before" | "after")
                      }
                      className="bg-gray-700 border border-gray-600 text-white px-3 py-2 rounded text-sm"
                    >
                      <option value="before">before name</option>
                      <option value="after">after name</option>
                    </select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Preview */}
            {bulkRenamingIds.length > 0 && (
              <div className="bg-gray-700 rounded p-3 space-y-2">
                <p className="text-xs text-gray-400 font-semibold">Preview:</p>
                {panoramas
                  .filter((p) => bulkRenamingIds.includes(p.id))
                  .slice(0, 3)
                  .map((panorama) => {
                    let newName = panorama.name;
                    if (bulkRenameMode === "replace" && bulkRenameFindText) {
                      newName = newName.replaceAll(bulkRenameFindText, bulkRenameReplaceText);
                    } else if (bulkRenameMode === "add" && bulkRenameAddText) {
                      if (bulkRenameAddPosition === "before") {
                        newName = bulkRenameAddText + newName;
                      } else {
                        newName = newName + bulkRenameAddText;
                      }
                    }
                    return (
                      <div key={panorama.id} className="text-xs">
                        <span className="text-gray-400">{panorama.name}</span>
                        {newName !== panorama.name && (
                          <>
                            <span className="text-gray-500 mx-1">→</span>
                            <span className="text-white">{newName}</span>
                          </>
                        )}
                      </div>
                    );
                  })}
                {bulkRenamingIds.length > 3 && (
                  <p className="text-xs text-gray-400">
                    ... and {bulkRenamingIds.length - 3} more
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowBulkRenameDialog(false);
                  setBulkRenamingIds([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => handleConfirmBulkRenamePanoramas()}
                disabled={
                  (bulkRenameMode === "replace" && !bulkRenameFindText) ||
                  (bulkRenameMode === "add" && !bulkRenameAddText)
                }
              >
                Rename
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
